import React from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AiOutlinePlus, AiOutlineDelete } from "react-icons/ai";
import Tooltip from "@mui/material/Tooltip";

// ✅ Validation schema for each country
const countrySchema = yup.object().shape({
  name: yup.string().required("Country name is required"),
});

// Form schema for array of countries
const schema = yup.object().shape({
  countries: yup.array().of(countrySchema).min(1, "At least one country is required"),
});

function CountryForm({ onSubmit, updateData }) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      countries: [{ name: "" }],
    },
  });

  // Populate form when editing
  React.useEffect(() => {
    if (updateData?.name) {
      reset({ countries: [updateData] });
    }
  }, [updateData, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "countries",
  });

  return (
    <div className="max-w-md mx-auto p-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="relative p-2 border-b rounded-md space-y-2"
          >
            <div className="font-semibold text-gray-700 mb-1">
              Country {index + 1}
            </div>
            <Controller
              name={`countries.${index}.name`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder="Enter country name"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            />
            {errors.countries?.[index]?.name && (
              <p className="text-red-500 text-sm mt-1">
                {errors.countries[index].name.message}
              </p>
            )}

            {/* Remove button */}
            {fields.length > 1 && (
              <Tooltip title="Remove Country" placement="top">
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="absolute top-0 right-2 text-red-500 hover:text-red-700"
                >
                  <AiOutlineDelete size={20} />
                </button>
              </Tooltip>
            )}
          </div>
        ))}

        <div className="flex gap-3">
          {!updateData && (
            <button
              type="button"
              onClick={() => append({ name: "" })}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <AiOutlinePlus size={16} />
            </button>
          )}
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
          >
            {updateData ? "Update Country" : "Add Country"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CountryForm;
